import {
  EphemeralCornerNotificationConfig,
  EphemeralModalNotificationConfig,
  EphemeralNotificationConfig,
  EphemeralNotificationKeys,
  EphemeralPopoverNotificationConfig,
} from './types';
import { parseDate } from '@ardoq/date-time';
import { I18nLocale } from '@ardoq/i18n';
import { isPresentationMode } from 'env';
import {
  getConditionalFormattingAiOnboardingContent,
  getLabelsAiOnboardingContent,
} from './notificationsContent/aiOnboarding';
import { getComponentMatrixInViewpointModeContent } from './notificationsContent/componentMatrixInViewpointMode';
import {
  getDataInventoryWhatsNewTourPromptContent,
  isDataInventoryWhatsNewTourRelevantForCurrentUser,
} from './notificationsContent/dataInventoryWhatsNewTour';
import {
  getDiscoverExperienceTourPromptContent,
  isDiscoverExperienceTourRelevantForCurrentUser,
} from './notificationsContent/discoverExperienceTour';

type EphemeralNotificationConfigFactory = (
  i18nLocale: I18nLocale
) => EphemeralNotificationConfig;

const getComponentMatrixInViewpointMode = (
  i18nLocale: I18nLocale
): EphemeralModalNotificationConfig => ({
  ...getComponentMatrixInViewpointModeContent(i18nLocale),
  kind: 'modal',
  id: EphemeralNotificationKeys.COMPONENT_MATRIX_IN_VIEWPOINT_MODE,
  relevantFromDate: parseDate('2026-07-22'),
  isRelevantForCurrentUser: () => !isPresentationMode(),
});

const getConditionalFormattingAiOnboarding = (
  i18nLocale: I18nLocale
): EphemeralPopoverNotificationConfig => ({
  ...getConditionalFormattingAiOnboardingContent(i18nLocale),
  kind: 'popover',
  id: EphemeralNotificationKeys.CONDITIONAL_FORMATTING_AI_ONBOARDING,
  relevantFromDate: parseDate('2026-08-06'),
  isRelevantForCurrentUser: () => true,
});

const getLabelsAiOnboarding = (
  i18nLocale: I18nLocale
): EphemeralPopoverNotificationConfig => ({
  ...getLabelsAiOnboardingContent(i18nLocale),
  kind: 'popover',
  id: EphemeralNotificationKeys.LABELS_AI_ONBOARDING,
  relevantFromDate: parseDate('2026-08-06'),
  isRelevantForCurrentUser: () => true,
});

const getDiscoverExperienceTourPrompt = (
  i18nLocale: I18nLocale
): EphemeralCornerNotificationConfig => ({
  ...getDiscoverExperienceTourPromptContent(i18nLocale),
  kind: 'corner',
  id: EphemeralNotificationKeys.DISCOVER_EXPERIENCE_TOUR,
  relevantFromDate: parseDate('2026-09-10'),
  isRelevantForCurrentUser: isDiscoverExperienceTourRelevantForCurrentUser,
});

const getDataInventoryWhatsNewTourPrompt = (
  i18nLocale: I18nLocale
): EphemeralCornerNotificationConfig => ({
  ...getDataInventoryWhatsNewTourPromptContent(i18nLocale),
  kind: 'corner',
  id: EphemeralNotificationKeys.DATA_INVENTORY_2026_10_UPDATES_WITH_TOUR,
  relevantFromDate: parseDate('2026-10-12'),
  relevantUntilDate: parseDate('2026-11-12'),
  isRelevantForCurrentUser: isDataInventoryWhatsNewTourRelevantForCurrentUser,
});

/** Rename-locked: the ephemeral-notification cleanup job locates this file by this name. */
const EPHEMERAL_NOTIFICATION_CONFIG_FACTORIES: ReadonlyArray<
  readonly [EphemeralNotificationKeys, EphemeralNotificationConfigFactory]
> = [
  [
    EphemeralNotificationKeys.COMPONENT_MATRIX_IN_VIEWPOINT_MODE,
    getComponentMatrixInViewpointMode,
  ],
  [
    EphemeralNotificationKeys.CONDITIONAL_FORMATTING_AI_ONBOARDING,
    getConditionalFormattingAiOnboarding,
  ],
  [EphemeralNotificationKeys.LABELS_AI_ONBOARDING, getLabelsAiOnboarding],
  [
    EphemeralNotificationKeys.DISCOVER_EXPERIENCE_TOUR,
    getDiscoverExperienceTourPrompt,
  ],
  [
    EphemeralNotificationKeys.DATA_INVENTORY_2026_10_UPDATES_WITH_TOUR,
    getDataInventoryWhatsNewTourPrompt,
  ],
];

export const EPHEMERAL_NOTIFICATION_KEYS: EphemeralNotificationKeys[] =
  EPHEMERAL_NOTIFICATION_CONFIG_FACTORIES.map(([key]) => key);

export const getEphemeralNotificationConfigs = (
  i18nLocale: I18nLocale
): Map<EphemeralNotificationKeys, EphemeralNotificationConfig> =>
  new Map(
    EPHEMERAL_NOTIFICATION_CONFIG_FACTORIES.map(([key, factory]) => [
      key,
      factory(i18nLocale),
    ])
  );
